import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHero } from "@/components/common/PageHero";
import { Reveal } from "@/components/common/Reveal";
import { ArrowRight, Clock } from "@phosphor-icons/react";
import { useT } from "@/context/LanguageContext";

export default function Blog() {
  const { t, lang } = useT();
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("All");
  useEffect(() => { api.get("/blog").then((r) => setPosts(r.data)).catch((e) => console.error("Failed to load blog:", e)); }, [lang]);

  const categories = ["All", ...Array.from(new Set(posts.map((p) => p.category)))];
  const shown = filter === "All" ? posts : posts.filter((p) => p.category === filter);
  const featured = shown[0];
  const rest = shown.slice(1);

  return (
    <div data-testid="blog-page">
      <PageHero
        overline={t("blogPage.heroOverline")}
        title={t("blogPage.heroTitle")}
        subtitle={t("blogPage.heroSubtitle")}
        crumbs={[{ label: t("nav.blog") }]}
        image="https://images.pexels.com/photos/577210/pexels-photo-577210.jpeg"
      />
      <section className="bg-white py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="mb-10 flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`blog-filter-${c}`}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                  filter === c ? "border-ink bg-ink text-white" : "border-slate-200 text-slate-600 hover:border-teal"
                }`}
              >
                {c === "All" ? t("blogPage.all") : c}
              </button>
            ))}
          </div>

          {featured && (
            <Reveal>
              <Link to={`/blog/${featured.slug}`} data-testid="blog-featured" className="group mb-12 grid overflow-hidden rounded-2xl border border-slate-200 lg:grid-cols-2">
                <div className="relative h-72 lg:h-full">
                  <img src={featured.cover_image} alt={featured.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="flex flex-col justify-center p-8 lg:p-12">
                  <span className="text-xs uppercase tracking-wider font-semibold text-teal">{featured.category}</span>
                  <h2 className="mt-3 font-heading text-2xl font-semibold leading-tight text-ink lg:text-3xl">{featured.title}</h2>
                  <p className="mt-4 text-sm leading-relaxed text-slate-600">{featured.excerpt}</p>
                  <span className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-ink">
                    {t("blogPage.readArticle")} <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          )}

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {rest.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 3) * 0.07}>
                <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${i}`} className="group flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                  <div className="relative h-48 overflow-hidden">
                    <img src={p.cover_image} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <span className="text-xs uppercase tracking-wider font-semibold text-teal">{p.category}</span>
                    <h3 className="mt-2 font-heading text-lg font-semibold leading-snug text-ink">{p.title}</h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600 line-clamp-3">{p.excerpt}</p>
                    <span className="mt-4 flex items-center gap-1.5 text-xs text-slate-400"><Clock size={14} /> {p.read_time}</span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
